import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, Button, Drawer, IconButton, Tab, Tabs } from '@mui/material';
import classnames from 'classnames';
import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { AppDispatch } from '../../../../../app/store';
import { SnackBarType } from '../../../../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../../../../layout/SnackbarContext';
import { drawerCoordActions } from '../../../drawer/drawerCoordSlice';
import ConfirmationModal from '../components/ConfirmationModal';
import SectionMetadataForm from '../components/SectionMetadataForm';
import UnsavedChangesModal from '../components/UnsavedChangesModal';
import useETIForm from '../hooks/useETIForm';
import { ETI, ETIFormData, isETIV2 } from '../types';
import { ETITextParsingError, textToQuestions } from '../utils';

import { normalizeQuestionsForSubmit } from '../utils/normalizeQuestionsForSubmit';
import styles from './ETIBuilderDrawer.module.scss';
import ETIBuilderForm from './ETIBuilderForm';
import { HANDLE_HEIGHT, useDrawerResize } from './useDrawerResize';

export interface ETIBuilderDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: ETIFormData) => Promise<boolean>;
  taskId: number;
  isLoading: boolean;
  eti: ETI | null;
}

export interface ETIBuilderDrawerHandle {
  expand: () => void;
  requestCloseAndThen: (onConfirmed: () => void) => void;
}

const ETIBuilderDrawer = forwardRef<
  ETIBuilderDrawerHandle,
  ETIBuilderDrawerProps
>(({ isOpen, onClose, onSubmit, taskId, isLoading, eti }, ref) => {
  const { control, handleSubmit, reset, getValues, setValue, isDirty } =
    useETIForm();
  const { openSnack } = useContext(SnackbarContext);
  const dispatch = useDispatch<AppDispatch>();
  const pendingCallbackRef = useRef<(() => void) | null>(null);

  const isReadOnly = useMemo(
    () =>
      eti?.status === 'PUBLISHED' ||
      eti?.status === 'SUBMITTED' ||
      eti?.status === 'WAIVED',
    [eti?.status],
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isDisabled = useMemo(
    () => isLoading || isSubmitting,
    [isLoading, isSubmitting],
  );
  const [publishConfirmData, setPublishConfirmData] =
    useState<ETIFormData | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const handleCollapseFromResize = useCallback(() => setIsExpanded(false), []);
  const { expandedHeight, isResizing, gripProps } = useDrawerResize({
    onCollapse: handleCollapseFromResize,
  });
  const handleToggleExpanded = useCallback((ev?: React.MouseEvent) => {
    ev?.stopPropagation();
    setIsExpanded(prev => !prev);
  }, []);
  const handleOpenPreview = useCallback(
    (ev: React.MouseEvent) => {
      ev.stopPropagation();
      window.open(
        `/workbench/eti/${taskId}/preview`,
        '_blank',
        'noopener,noreferrer',
      );
    },
    [taskId],
  );
  const handleOpenAnswer = useCallback(
    (ev: React.MouseEvent) => {
      ev.stopPropagation();
      window.open(
        `/workbench/eti/${taskId}/answer`,
        '_blank',
        'noopener,noreferrer',
      );
    },
    [taskId],
  );

  const watchedQuestions = useWatch({ control, name: 'questions' }) ?? [];
  const questionCount = watchedQuestions.length;
  const addQuestionRef = useRef<(() => void) | null>(null);
  const handleAddQuestionReady = useCallback((addFn: () => void) => {
    addQuestionRef.current = addFn;
  }, []);

  const statusLabel = eti?.status ?? 'DRAFT';
  const countLabel = `${questionCount} question${questionCount === 1 ? '' : 's'}`;

  const handleCloseAttempt = useCallback(() => {
    if (isDirty && !isReadOnly) {
      setShowUnsavedChangesModal(true);
      return;
    }
    reset({ questions: [], sectionMetadata: [] });
    setSelectedTab(0);
    onClose();
  }, [onClose, reset, isDirty, isReadOnly]);

  const handleCloseClick = useCallback(
    (ev: React.MouseEvent) => {
      ev.stopPropagation();
      handleCloseAttempt();
    },
    [handleCloseAttempt],
  );

  const handleForceClose = useCallback(() => {
    reset({ questions: [], sectionMetadata: [] });
    setSelectedTab(0);
    onClose();
  }, [onClose, reset]);

  const handleDiscardChanges = useCallback(() => {
    reset({ questions: [], sectionMetadata: [] });
    setSelectedTab(0);
    setShowUnsavedChangesModal(false);
    const callback = pendingCallbackRef.current;
    pendingCallbackRef.current = null;
    if (callback) {
      callback();
    } else {
      onClose();
    }
  }, [onClose, reset]);

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: number) => {
      setSelectedTab(newValue);
    },
    [],
  );

  const handleResetForm = useCallback(() => {
    reset({ questions: [], sectionMetadata: [] });
  }, [reset]);

  const handleAddQuestion = useCallback(() => {
    addQuestionRef.current?.();
  }, []);

  const handleOnSave = useCallback(
    async (formData: Omit<ETIFormData, 'taskId' | 'status'>) => {
      setIsSubmitting(true);
      try {
        const normalized = normalizeQuestionsForSubmit(formData);
        const success = await onSubmit({
          ...normalized,
          status: 'DRAFT',
          taskId,
        });
        if (success) {
          handleForceClose();
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [taskId, onSubmit, handleForceClose],
  );

  const handleOnPublish = useCallback(
    (formData: Omit<ETIFormData, 'taskId' | 'status'>) =>
      setPublishConfirmData({
        ...normalizeQuestionsForSubmit(formData),
        status: 'PUBLISHED',
        taskId,
      }),
    [taskId],
  );

  const handleOnPublishConfirm = useCallback(async () => {
    if (!publishConfirmData) return;
    setIsSubmitting(true);
    try {
      const success = await onSubmit(publishConfirmData);
      if (success) {
        handleForceClose();
        setPublishConfirmData(null);
      } else {
        setPublishConfirmData(null);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [publishConfirmData, handleForceClose, onSubmit]);

  useEffect(() => {
    dispatch(drawerCoordActions.syncEtiDirty({ taskId, isDirty }));
  }, [isDirty, taskId, dispatch]);

  const handleOnPaste = useCallback(
    (event: ClipboardEvent) => {
      if (!isOpen || isDisabled || isReadOnly || selectedTab !== 0) return;
      const pastedText = event.clipboardData?.getData('text');
      if (!pastedText) return;
      try {
        const questions = textToQuestions(pastedText);
        if (questions.length > 0) {
          const currentQuestions = getValues('questions');
          setValue('questions', [...currentQuestions, ...questions], {
            shouldDirty: true,
          });
          event.preventDefault();
        }
      } catch (error) {
        if (error instanceof ETITextParsingError) {
          event.preventDefault();
          openSnack({
            id: Date.now(),
            type: SnackBarType.ALERT,
            message: (error as ETITextParsingError).message,
            autoHideDuration: 6000,
          });
        } else {
          console.error('Failed to parse pasted ETI data', error);
        }
      }
    },
    [isOpen, isDisabled, isReadOnly, selectedTab, getValues, setValue, openSnack],
  );

  useEffect(() => {
    document.addEventListener('paste', handleOnPaste);
    return () => document.removeEventListener('paste', handleOnPaste);
  }, [handleOnPaste]);

  useEffect(() => {
    if (!eti || !isOpen) return;
    reset({
      questions: (eti.questions.questions || []).map(q => ({
        ...q,
        optionsRaw: q?.options ? JSON.stringify(q.options) : '',
        filterRaw: (q as any)?.filter ? JSON.stringify((q as any).filter) : '',
      })) as any,
      sectionMetadata: eti.questions.sectionMetadata || [],
    });
    setSelectedTab(0);
  }, [eti, isOpen, reset]);

  useEffect(() => {
    if (isOpen) setIsExpanded(true);
  }, [isOpen]);

  useImperativeHandle(
    ref,
    () => ({
      expand: () => setIsExpanded(true),
      requestCloseAndThen: (onConfirmed: () => void) => {
        if (isDirty && !isReadOnly) {
          pendingCallbackRef.current = onConfirmed;
          setShowUnsavedChangesModal(true);
        } else {
          reset({ questions: [], sectionMetadata: [] });
          onClose();
          onConfirmed();
        }
      },
    }),
    [isDirty, isReadOnly, reset, onClose],
  );

  const drawerHeight = isExpanded ? expandedHeight : HANDLE_HEIGHT;

  // Reserve bottom space on <body> so the page can scroll past the drawer
  // and the content underneath it is reachable. useLayoutEffect so cleanup
  // runs synchronously (no flicker; assertions in tests see it cleared).
  useLayoutEffect(() => {
    if (!isOpen) return;
    document.body.style.paddingBottom = `${drawerHeight}px`;
    return () => {
      document.body.style.paddingBottom = '';
    };
  }, [isOpen, drawerHeight]);

  return (
    <>
      <Drawer
        anchor="bottom"
        open={isOpen}
        variant="persistent"
        PaperProps={{
          className: styles.paper,
          style: {
            height: drawerHeight,
            transition: isResizing ? 'none' : 'height 150ms ease',
          },
        }}
      >
        {isExpanded && (
          <div
            data-tid="eti-drawer-resize-grip"
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize ETI drawer"
            {...gripProps}
            className={classnames(
              styles.resizeGrip,
              isResizing && styles.resizeGripActive,
            )}
            title="Drag to resize · double-click to reset"
          />
        )}
        <div
          data-tid="eti-drawer-handle"
          onClick={handleToggleExpanded}
          className={styles.handle}
        >
          <div className={styles.handleLeft}>
            <LockOutlinedIcon sx={{ fontSize: 16, color: '#1a1a18' }} />
            <span className={styles.title}>ETI builder</span>
            <span
              className={classnames(
                styles.chip,
                statusLabel === 'PUBLISHED'
                  ? styles.chipPublished
                  : styles.chipDraft,
              )}
            >
              {`${countLabel} · ${statusLabel}`}
            </span>
            {isDirty && !isReadOnly && statusLabel === 'DRAFT' && (
              <span className={styles.dirtyDot} title="Unsaved changes" />
            )}
          </div>
          <div className={styles.handleRight}>
            {eti &&
              isETIV2(eti) &&
              eti.questions.questions.length > 0 &&
              eti.status !== 'SUBMITTED' && (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={isDisabled}
                  onClick={handleOpenPreview}
                >
                  Preview
                </Button>
              )}
            {eti && isETIV2(eti) && eti.status === 'SUBMITTED' && (
              <Button
                size="small"
                variant="outlined"
                disabled={isDisabled}
                onClick={handleOpenAnswer}
              >
                View Response
              </Button>
            )}
            <span className={styles.hint}>
              {isExpanded ? 'Click to collapse' : 'Click to expand'}
            </span>
            <IconButton
              size="small"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
              onClick={handleToggleExpanded}
            >
              {isExpanded ? (
                <ExpandMoreIcon fontSize="small" sx={{ color: '#6b6a65' }} />
              ) : (
                <ExpandLessIcon fontSize="small" sx={{ color: '#6b6a65' }} />
              )}
            </IconButton>
            <IconButton
              size="small"
              aria-label="Close ETI builder"
              onClick={handleCloseClick}
            >
              ×
            </IconButton>
          </div>
        </div>

        {isExpanded && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div className={styles.tabsBorder}>
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                TabIndicatorProps={{ className: styles.tabIndicator }}
                classes={{ root: styles.tabsRoot }}
              >
                <Tab label="Questions" />
                <Tab label="Section Details" />
              </Tabs>
            </div>
            <div className={styles.body}>
              {selectedTab === 0 && (
                <ETIBuilderForm
                  isLoading={isLoading}
                  isReadOnly={isReadOnly}
                  isDisabled={isDisabled}
                  control={control as any}
                  setValue={setValue as any}
                  answers={eti?.answers?.answers ?? []}
                  isVersion2={isETIV2(eti)}
                  onAddQuestionReady={handleAddQuestionReady}
                />
              )}
              {selectedTab === 1 && (
                <SectionMetadataForm
                  isLoading={isLoading}
                  isReadOnly={isReadOnly}
                  isDisabled={isDisabled}
                  control={control as any}
                />
              )}
            </div>
            {!isReadOnly && (
              <div className={styles.footer}>
                <span
                  className={styles.questionCount}
                  data-tid="eti-drawer-question-count"
                >
                  {countLabel}
                </span>
                <Button
                  variant="outlined"
                  disabled={isDisabled || questionCount === 0}
                  onClick={handleResetForm}
                  className={styles.btnReset}
                >
                  Reset form
                </Button>
                <Button
                  variant="contained"
                  disabled={isDisabled || selectedTab !== 0}
                  onClick={handleAddQuestion}
                  className={styles.btnAddQuestion}
                >
                  + Add question
                </Button>
                <Button
                  variant="outlined"
                  disabled={isDisabled}
                  onClick={handleSubmit(handleOnSave)}
                  className={styles.btnSave}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  disabled={isDisabled}
                  onClick={handleSubmit(handleOnPublish)}
                  className={styles.btnPublish}
                >
                  Publish
                </Button>
              </div>
            )}
          </Box>
        )}
      </Drawer>
      <ConfirmationModal
        isOpen={!!publishConfirmData}
        onClose={() => setPublishConfirmData(null)}
        onSubmit={handleOnPublishConfirm}
      />
      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        onClose={() => {
          setShowUnsavedChangesModal(false);
          pendingCallbackRef.current = null;
        }}
        onDiscard={handleDiscardChanges}
      />
    </>
  );
});

ETIBuilderDrawer.displayName = 'ETIBuilderDrawer';

export default ETIBuilderDrawer;
